import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useCreatePublicBooking } from './useCreatePublicBooking';
import { lookupErrorCopy } from '../lib/errorMap';

interface ServiceOption { id: string; name: string; duration_minutes: number; price: number; }

interface CreateBookingData {
  business_id: string;
  created_by: string;
  service_id?: string;
  service_name?: string;
  client_id?: string;
  client_name: string;
  client_phone?: string;
  assigned_id?: string;
  date: string;
  start_time: string;
  end_time: string;
  payment_amount?: number;
  booked_price?: number | null;
  notes?: string;
}

export function useCreateBooking(businessId: string | undefined) {
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const createPublicBooking = useCreatePublicBooking();

  useEffect(() => {
    if (!businessId) return;
    supabase.from('services').select('id, name, duration_minutes, price')
      .eq('business_id', businessId).eq('is_active', true).order('name')
      .then(({ data }) => setServices((data ?? []) as ServiceOption[]));
  }, [businessId]);

  const createBooking = useCallback(async (data: CreateBookingData): Promise<boolean> => {
    setLoading(true); setError(null);
    try {
      if (!data.client_name.trim()) { setError('Client name is required'); setLoading(false); return false; }
      if (!data.date) { setError('Date is required'); setLoading(false); return false; }
      if (!data.service_id) { setError('Service is required'); setLoading(false); return false; }

      // create_public_booking(id-variant) dual-writes bookings + schedule_entries
      // + pending_notifications + activity_log server-side. Source 'admin_panel'
      // maps to 'phone' (admin staff booking on behalf of client). Extra fields
      // (payment_amount/booked_price/price_type/notes/end_time/status) are not
      // accepted by the id-variant signature today — queued in
      // §Schedule.AdminBookingExtraFieldsLift.
      const res = await createPublicBooking({
        businessId: data.business_id,
        serviceId: data.service_id,
        date: data.date,
        startTime: data.start_time,
        clientName: data.client_name.trim(),
        clientPhone: data.client_phone || '',
        operatorId: data.assigned_id || undefined,
        source: 'phone',
        idempotencyKey: crypto.randomUUID(),
      });
      if (!res.success) {
        setError(lookupErrorCopy('create_public_booking', res.error.code));
        setLoading(false);
        return false;
      }
      setLoading(false);
      return true;
    } catch (err) { setError(err instanceof Error ? err.message : 'Failed to create booking'); setLoading(false); return false; }
  }, [createPublicBooking]);

  return { createBooking, services, loading, error };
}
